import { __makeTemplateObject, __spreadArray, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { themeGet, Box, ActionList, Text, ActionMenu } from '@primer/react';
import { createUniqueID } from '../../utils/functions.js';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';
import { LTTextInput } from '../LTInputBox/index.js';

/**
 * @param {string} customButtonText Custom text for the dropdown button (optional) - if not provided, the default header will be "View: {selectedOption}"
 * @param {!Array<!Object<string, *>} options Dropdown options in the menu. Each key will have the following
 * - name: string
 * - value: string
 * - subText: string (optional)
 * - isGeneric: boolean (optional) - if true, the option will be treated as a generic option and overrides all options
 * @param {boolean} wrapListText Wrap the text in the dropdown list
 */
var ListItemSpan = styled.span(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  color: ", ";\n  word-break: break-all;\n  ", "\n"], ["\n  color: ", ";\n  word-break: break-all;\n  ", "\n"])), themeGet("colors.text.default"), function (_a) {
  var wrapListText = _a.wrapListText;
  return !wrapListText && "\n    display: block;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n  ";
});
var ListItemSubSpan = styled.span(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  font-size: 12px;\n  color: ", ";\n"], ["\n  font-size: 12px;\n  color: ", ";\n"])), themeGet("colors.text.muted"));
var StyledBox = styled(Box)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: ", " ", ";\n"], ["\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: ", " ", ";\n"])), themeGet("space.1"), themeGet("space.2"));
var LTSelect = function LTSelect(_a) {
  var _b;
  var _c = _a.anchorButtonSize,
    anchorButtonSize = _c === void 0 ? LTSelect.AnchorButtonSize.MEDIUM : _c,
    _d = _a.buttonStyle,
    buttonStyle = _d === void 0 ? {} : _d,
    _e = _a.buttonId,
    buttonId = _e === void 0 ? null : _e,
    _f = _a.buttonProps,
    buttonProps = _f === void 0 ? {} : _f,
    _g = _a.customButtonClassName,
    customButtonClassName = _g === void 0 ? "" : _g,
    _h = _a.customButtonText,
    customButtonText = _h === void 0 ? "" : _h,
    _j = _a.defaultSelections,
    defaultSelections = _j === void 0 ? [] : _j,
    _k = _a.disabled,
    disabled = _k === void 0 ? false : _k,
    _l = _a.isMultiSelect,
    isMultiSelect = _l === void 0 ? false : _l,
    _m = _a.leadingText,
    leadingText = _m === void 0 ? "View" : _m,
    _o = _a.itemStyles,
    itemStyles = _o === void 0 ? {} : _o,
    _p = _a.inputAriaLabel,
    inputAriaLabel = _p === void 0 ? undefined : _p,
    _q = _a.listStyle,
    listStyle = _q === void 0 ? {} : _q,
    _r = _a.options,
    options = _r === void 0 ? [] : _r,
    _s = _a.onChange,
    onChange = _s === void 0 ? function () {} : _s,
    _t = _a.overlaySize,
    overlaySize = _t === void 0 ? "small" : _t,
    _u = _a.searchPlaceholder,
    searchPlaceholder = _u === void 0 ? "" : _u,
    _v = _a.tabIndex,
    tabIndex = _v === void 0 ? 0 : _v,
    _w = _a.title,
    title = _w === void 0 ? "" : _w,
    _x = _a.withSearch,
    withSearch = _x === void 0 ? false : _x,
    _y = _a.wrapListText,
    wrapListText = _y === void 0 ? false : _y;
  var defaultOptionsToSelect = (defaultSelections === null || defaultSelections === void 0 ? void 0 : defaultSelections.length) > 0 ? defaultSelections : [options === null || options === void 0 ? void 0 : options[0]];
  var _z = useState(__spreadArray([], defaultOptionsToSelect, true)),
    selectedOptions = _z[0],
    setSelectedOptions = _z[1];
  var _0 = useState(""),
    searchValue = _0[0],
    setSearchValue = _0[1];
  var _1 = useState(__spreadArray([], options, true)),
    filteredOptions = _1[0],
    setFilteredOptions = _1[1];
  var _2 = useState(false),
    isMenuOpen = _2[0],
    setIsMenuOpen = _2[1];
  var anchorRef = useRef(null);
  useEffect(function () {
    if (isMenuOpen) {
      setTimeout(function () {
        var elems = document.getElementsByClassName("sentinel");
        for (var i = 0; i < (elems === null || elems === void 0 ? void 0 : elems.length); i++) {
          elems[i].setAttribute("tabindex", "-1");
        }
      }, 0);
    }
  }, [isMenuOpen]);
  useEffect(function () {
    if (anchorRef.current && tabIndex !== 0) {
      // This is done to set the tabIndex of the anchor button
      // primer has a bug where it overrides the tabIndex to 0
      anchorRef.current.tabIndex = tabIndex;
    }
  }, []);
  useEffect(function () {
    if (defaultSelections.length > 0) {
      if (!isEqual(defaultSelections, selectedOptions)) {
        setSelectedOptions(__spreadArray([], defaultSelections, true));
      }
    }
  }, [defaultSelections]); //TODO: validate if deps should have this for uncontrolled version
  useEffect(function () {
    if (searchValue) {
      var filteredOptions_1 = options.filter(function (option) {
        return option.name.toLowerCase().includes(searchValue.toLowerCase());
      });
      setFilteredOptions(filteredOptions_1);
    } else {
      setFilteredOptions(options);
    }
  }, [options, searchValue]);
  useEffect(function () {
    if (isMenuOpen) {
      var elements = document.getElementsByClassName("actionListGroup-".concat(title || "noTitle"));
      if (elements.length > 0 && elements[0].children.length > 0) {
        // remove attrributes to make the title visible to screen readers
        // default behaviour is to hide the title from screen readers and announce the group
        elements[0].children[0].removeAttribute("aria-hidden");
        elements[0].children[0].removeAttribute("role");
      }
      setSearchValue("");
      setFilteredOptions(options);
    }
  }, [isMenuOpen]);
  var onSelectChange = function onSelectChange(e, option) {
    if (isMultiSelect) {
      e.preventDefault(); // prevents overlay from closing
    }

    var updatedSelectedOptions = __spreadArray([], selectedOptions, true);
    if (!isMultiSelect || option.isGeneric) {
      updatedSelectedOptions = [option];
    } else {
      // If it's a multi-select and the option is not generic,
      // we handle multiple selections.
      // Find the index of the generic option in the updatedSelectedOptions array.
      var genericOptionIndex = updatedSelectedOptions.findIndex(function (opt) {
        return opt.isGeneric;
      });
      // If a generic option is found, remove it from the array.
      if (genericOptionIndex > -1) {
        updatedSelectedOptions.splice(genericOptionIndex, 1);
      }
      var index = updatedSelectedOptions.findIndex(function (obj) {
        return (option === null || option === void 0 ? void 0 : option.value) === (obj === null || obj === void 0 ? void 0 : obj.value);
      });
      // If the option is already selected, remove it.
      if (index > -1) {
        updatedSelectedOptions.splice(index, 1);
        // If there are no more selected options, add the generic option back.
        if (updatedSelectedOptions.length === 0) {
          var genericOption = options.find(function (opt) {
            return opt.isGeneric;
          });
          if (genericOption) {
            updatedSelectedOptions.push(genericOption);
          }
        }
      } else {
        // If the option is not selected, add it to the selected options.
        updatedSelectedOptions.push(option);
      }
    }
    setSelectedOptions(updatedSelectedOptions);
    onChange(updatedSelectedOptions);
  };
  var getTitle = function getTitle() {
    var _a;
    if (!selectedOptions.length) {
      return "None";
    }
    if (selectedOptions.length > 1) {
      return "".concat(selectedOptions.length, " Selected");
    }
    return (_a = options.find(function (option) {
      var _a;
      return (option === null || option === void 0 ? void 0 : option.value) === ((_a = selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions[0]) === null || _a === void 0 ? void 0 : _a.value);
    })) === null || _a === void 0 ? void 0 : _a.name;
  };
  var anchorId = createUniqueID("lt-select");
  var ActionListGroupWrapper = useMemo(function () {
    return function (_a) {
      var children = _a.children;
      var shouldRenderGroup = title || withSearch;
      return /*#__PURE__*/React.createElement(React.Fragment, null, shouldRenderGroup && /*#__PURE__*/React.createElement(ActionList.Group, {
        className: "actionListGroup-".concat(title || "noTitle"),
        sx: {
          "& > div": {
            borderBottom: "1px solid",
            borderColor: "border.default",
            paddingTop: "0",
            paddingLeft: themeGet("space.2"),
            paddingRight: themeGet("space.2"),
            marginBottom: themeGet("space.2")
          },
          "& > ul": {
            overflowY: "auto",
            maxHeight: "330px",
            padding: "2px 0"
          }
        },
        title: /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(StyledBox, null, /*#__PURE__*/React.createElement(Text, {
          color: "fg.default",
          role: "heading",
          "aria-level": "2",
          fontSize: 14
        }, title)), withSearch && /*#__PURE__*/React.createElement(LTTextInput, {
          ariaLabel: inputAriaLabel,
          onChange: function onChange(text) {
            setSearchValue(text);
          },
          placeholder: "".concat(searchPlaceholder || "Search ".concat((title === null || title === void 0 ? void 0 : title.toLowerCase()) || "")),
          value: searchValue,
          trailingAction: function trailingAction() {
            return setSearchValue("");
          },
          extraStyle: {
            margin: "4px 0"
          }
        }))
      }, children), !shouldRenderGroup && children);
    };
  }, [title, withSearch, searchPlaceholder]);
  return /*#__PURE__*/React.createElement(ActionMenu, {
    sx: {
      background: "#000",
      zIndex: "999"
    },
    onOpenChange: function onOpenChange(open) {
      setIsMenuOpen(open);
    },
    anchorRef: anchorRef
  }, /*#__PURE__*/React.createElement(ActionMenu.Button, __assign({
    "aria-haspopup": true,
    "aria-expanded": isMenuOpen,
    "aria-controls": isMenuOpen ? anchorId : undefined,
    disabled: disabled,
    id: buttonId,
    sx: __assign({
      borderColor: "border.default",
      '[data-component="trailingAction"]': {
        color: "fg.default"
      }
    }, buttonStyle),
    tabIndex: tabIndex,
    size: anchorButtonSize,
    className: "lt-select-anchor ".concat(customButtonClassName)
  }, buttonProps), customButtonText || "".concat(leadingText ? "".concat(leadingText, ":") : "", " ").concat(getTitle())), /*#__PURE__*/React.createElement(ActionMenu.Overlay, {
    id: anchorId,
    role: "dialog",
    "aria-modal": "true",
    width: overlaySize,
    "aria-label": "".concat(title !== null && title !== void 0 ? title : "", " Menu"),
    className: "lt-select-overlay"
  }, /*#__PURE__*/React.createElement(ActionList, {
    selectionVariant: "single",
    role: "menu",
    sx: __assign((_b = {}, _b["@media only screen and (max-width: 1024px)"] = {
      maxHeight: "250px",
      overflow: "auto"
    }, _b["@media only screen and (max-width: 320px)"] = {
      maxHeight: "100px"
    }, _b), listStyle)
  }, /*#__PURE__*/React.createElement(ActionListGroupWrapper, null, filteredOptions.map(function (option, index) {
    if (!option) {
      return null;
    }
    var name = option.name,
      value = option.value,
      subText = option.subText,
      trailingVisual = option.trailingVisual;
    var _id = "selectItem-".concat(name.replace(/ /g, ""), "-").concat(index);
    return /*#__PURE__*/React.createElement(ActionList.Item, {
      "aria-labelledby": _id,
      id: value,
      key: "".concat(name, "-").concat(index),
      sx: __assign(__assign(__assign({}, trailingVisual && {
        // dom updates when trailingVisual is present
        "& > div > div > span": {
          display: "flex",
          flexDirection: "column"
        }
      }), !trailingVisual && {
        "& > div > span": {
          display: "flex",
          flexDirection: "column"
        }
      }), itemStyles),
      selected: !!selectedOptions.find(function (el) {
        return value === (el === null || el === void 0 ? void 0 : el.value);
      }),
      onSelect: function onSelect(e) {
        return onSelectChange(e, option);
      },
      role: "menuitemradio"
    }, /*#__PURE__*/React.createElement(ListItemSpan, {
      id: _id,
      wrapListText: wrapListText
    }, name), subText && /*#__PURE__*/React.createElement(ListItemSubSpan, null, " ", subText, " "), trailingVisual && /*#__PURE__*/React.createElement(ActionList.TrailingVisual, null, trailingVisual));
  })))));
};
LTSelect.AnchorButtonSize = {
  LARGE: "large",
  MEDIUM: "medium",
  SMALL: "small"
};
var templateObject_1, templateObject_2, templateObject_3;

export { LTSelect as default };
